import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useData } from "../../../context/DataContext.jsx";
import { needsCalendarReconcile } from "../../../lib/calendarSync.js";
import { openUnlessCancelled } from "../../../lib/quotePersistence.js";
import { amendmentVersions } from "../../../lib/quoteVersions.js";
import { C, font, mono, btnS, btnD, lbl as lblStyle, inp as inpStyle } from "../../../lib/styles.js";
import {
  JOB_STATUSES, JOB_STATUS_LABELS, JOB_STATUS_COLORS, INDUSTRY_LABELS,
  SESSION_LOCATION_FIELDS, STUDIO_ADDRESS_PARTS,
  SESSION_TYPE_LABELS, SESSION_TYPE_COLORS,
} from "../../../lib/constants.js";
import {
  fmtTime, fmtDate, FMT_DATE_FULL, FMT_DATE_LONG, FMT_DATE_DOW, addressMapQuery,
  sortSessions, BLANK_SESSION,
  SESSION_CAPTURE_FIELDS, studioPatch, clearLocationPatch,
} from "../../../lib/utils.js";
import { scopeJobToSession } from "../../../lib/sessionScope.js";
import LinkedContacts from "./jobcard/LinkedContacts.jsx";
import LocationInputs from "./jobcard/LocationInputs.jsx";
import SessionStrip from "./jobcard/SessionStrip.jsx";
import SessionEditor from "./jobcard/SessionEditor.jsx";
import AddDayMenu from "./jobcard/AddDayMenu.jsx";
import DeleteSessionConfirm from "./jobcard/DeleteSessionConfirm.jsx";
import JobPrepTab from "./jobcard/JobPrepTab.jsx";
import CaptureLog from "./jobcard/CaptureLog.jsx";
import DeliverableRequirementsTab from "./jobcard/DeliverableRequirementsTab.jsx";
import ProcessingTab from "./jobcard/ProcessingTab.jsx";
import DeliveryTab from "./jobcard/DeliveryTab.jsx";
import AssetsList from "./jobcard/AssetsList.jsx";
import FinancialsTab from "./jobcard/FinancialsTab.jsx";
import DocsFilesTab from "./jobcard/DocsFilesTab.jsx";
import CrewFilesTab from "./jobcard/CrewFilesTab.jsx";
import QuoteVersionHistoryModal from "./QuoteVersionHistoryModal.jsx";
import { jobAccess } from "../lib/jobAccess.js";
import { stripBinMeta } from "../lib/captureBin.js";

const JSC = JOB_STATUS_COLORS(C);

// The full tab set. Which of these a given role actually sees is decided by
// access.visibleTabs (the single source of truth in jobAccess.js) — never by a
// separate per-role list here, which would drift from the capability model.
const TABS_ALL = [
  { key: "jobCard",    label: "Job Card",        ships: null    },
  { key: "delivReq",   label: "Deliverable Req", ships: "J5"    },
  { key: "preProd",    label: "Prep",            ships: "J5"    },
  { key: "capture",    label: "Capture",         ships: "J6"    },
  { key: "processing", label: "Processing",      ships: "J7"    },
  { key: "delivery",   label: "Delivery",        ships: "J7"    },
  { key: "docsFiles",  label: "Docs & Files",    ships: "J15"   },
  { key: "financials", label: "Financials",      ships: "J17"   },
  // Crew-only (not in jobAccess ALL_TABS): read-only crew-visible files.
  { key: "files",      label: "Files",           ships: null    },
];

export default function JobDetailStub({ jobId, user, onOpenJob, onOpenBuilder }) {
  const {
    jobs, contacts, clients, savedQuotes,
    updateJob, updateJobStatus, toggleJobMilestone,
    deleteJob, unbookJob, unlinkQuote, loadQuote,
  } = useData();

  // Single source of truth for role-based access (see jobAccess.js). Phase 1
  // wires the real role; capability-based gating of money/contacts/tabs/edits
  // lands in phases 2-3. `isCrew` is derived here to keep this phase invisible
  // for admins.
  const access = jobAccess(user?.role);
  const isCrew = access.isCrew;
  // Label stamped on a soft-deleted capture row ("deleted by …"). Crew get a
  // "(crew)" suffix so the bin reads clearly; falls back to a role word.
  const currentUserLabel = useMemo(() => {
    const name = user?.name || user?.email || "";
    if (!name) return access.isAdmin ? "Admin" : "Crew";
    return access.isCrew ? `${name} (crew)` : name;
  }, [user?.name, user?.email, access.isAdmin, access.isCrew]);

  const job = jobs.find(j => j.id === jobId);

  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("jobCard");
  const [confirm, setConfirm] = useState(null); // "unlink" | "unbook" | "delete" | null
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [showQuoteVersions, setShowQuoteVersions] = useState(false);

  // Tabs this role may see (single source of truth: access.visibleTabs).
  const tabs = useMemo(() => TABS_ALL.filter(t => access.visibleTabs.includes(t.key)), [access.visibleTabs]);
  // safeActiveTab: never render the body of a tab this role can't see. Guards
  // against a stale activeTab pointing at a now-hidden tab.
  const activeTabKey = tabs.some(t => t.key === activeTab) ? activeTab : (tabs[0]?.key || "jobCard");
  const activeTabSpec = tabs.find(t => t.key === activeTabKey) || tabs[0];

  const parentJob = useMemo(
    () => job?.changeOrderOf ? jobs.find(jj => jj.id === job.changeOrderOf) : null,
    [job?.changeOrderOf, jobs]
  );
  // Change orders that point AT this job (parent → children direction).
  const childOrders = useMemo(
    () => (job?.id ? jobs.filter(jj => jj.changeOrderOf === job.id) : []),
    [job?.id, jobs]
  );
  const linkedQuote = useMemo(() => {
    if (!job?.quoteId) return null;
    return (savedQuotes || []).find(qq => qq.id === job.quoteId) || null;
  }, [job?.quoteId, savedQuotes]);
  const amendVersions = useMemo(() => amendmentVersions(linkedQuote?.versions).length, [linkedQuote]);

  // Single-day root→sessions[0] mirroring lives inside updateJob in DataContext — calling code
  // just writes root fields and the setter syncs sessions[0] against the latest state.
  const upd = useCallback((field, value) => {
    if (!job) return;
    updateJob(job.id, { [field]: value });
  }, [job, updateJob]);

  const applyStudio = useCallback((goStudio) => {
    if (!job) return;
    updateJob(job.id, goStudio ? studioPatch() : clearLocationPatch());
  }, [job, updateJob]);

  const sortedSessionsForTabs = useMemo(() => sortSessions(job?.sessions || []), [job?.sessions]);
  const tabSelectableSessions = useMemo(() => {
    if (!job?.multiDay) return [];
    if (activeTabKey === "capture" || activeTabKey === "processing" || activeTabKey === "delivery") {
      const captureSessions = sortedSessionsForTabs.filter(s => s?.sessionType === "capture");
      return captureSessions.length ? captureSessions : sortedSessionsForTabs;
    }
    return sortedSessionsForTabs;
  }, [activeTabKey, job?.multiDay, sortedSessionsForTabs]);
  const sessionForTabs = useMemo(() => {
    if (!job?.multiDay) return null;
    const selected = tabSelectableSessions.find(s => s?.id === selectedSessionId);
    if (activeTabKey === "capture") {
      if (selected?.sessionType === "capture") return selected;
      return tabSelectableSessions.find(s => s?.sessionType === "capture") || selected || tabSelectableSessions[0] || null;
    }
    return selected || tabSelectableSessions.find(s => s?.sessionType === "capture") || tabSelectableSessions[0] || null;
  }, [activeTabKey, job?.multiDay, selectedSessionId, tabSelectableSessions]);
  const tabScope = useMemo(
    () => job?.multiDay && sessionForTabs ? scopeJobToSession(job, sessionForTabs.id) : null,
    [job, sessionForTabs]
  );
  const tabJob = tabScope?.scopedJob || job;
  const updateTabJob = useCallback((patch) => {
    if (!job) return;
    if (!tabScope) {
      updateJob(job.id, patch);
      return;
    }
    const update = tabScope.buildUpdate(patch);
    if (update.sessions) update.sessions = sortSessions(update.sessions);
    updateJob(job.id, update);
  }, [job, tabScope, updateJob]);

  // Restore a capture row out of the job-level Recently Deleted bin back onto the
  // day it was deleted from. The bin (`captureLogDeleted`) is a ROOT field; the
  // live `captureLog` is session-scoped on multi-day jobs — so we route the row
  // back through the ORIGIN session (`deletedFromSessionId`), not the day in view.
  const restoreCaptureRow = useCallback((row) => {
    if (!job || !row) return;
    const bin = (job.captureLogDeleted || []).filter(d => d.id !== row.id);
    const clean = stripBinMeta(row);
    const sid = row.deletedFromSessionId;
    if (job.multiDay && sid) {
      const scope = scopeJobToSession(job, sid);
      if (scope) {
        const update = scope.buildUpdate({ captureLog: [...(scope.scopedJob.captureLog || []), clean] });
        update.captureLogDeleted = bin;
        if (update.sessions) update.sessions = sortSessions(update.sessions);
        updateJob(job.id, update);
        return;
      }
    }
    // Single-day, root-level, or an origin session that no longer exists → root log.
    updateJob(job.id, { captureLog: [...(job.captureLog || []), clean], captureLogDeleted: bin });
  }, [job, updateJob]);

  if (!job) {
    return (
      <div style={{ fontFamily: font, padding: 32, textAlign: "center", color: C.muted, fontSize: 13 }}>
        Job not found.
      </div>
    );
  }

  // Date shown in the always-visible header: the viewed day on multi-day jobs
  // (so you know WHICH day you're on), else the job's capture date.
  const headerDate = job.multiDay ? (sessionForTabs?.date || job.captureDate) : job.captureDate;

  return (
    <div style={{ fontFamily: font, animation: "fadeUp 0.3s ease" }}>
      {/* Persistent job identity — stays visible on every tab so you always know which job you're in */}
      <div style={S.jobHeader}>
        <span style={S.jobHeaderClient}>{job.clientName || "Untitled job"}</span>
        <span style={S.jobHeaderMeta}>
          {job.jobId || "no-id"}
          {headerDate ? ` · ${fmtDate(headerDate, FMT_DATE_DOW)}` : ""}
          {job.multiDay ? " · multi-day" : ""}
        </span>
        {childOrders.length > 0 && (
          <span style={S.changeOrderBadge}>
            {childOrders.length} change order{childOrders.length > 1 ? "s" : ""}
          </span>
        )}
      </div>

      {parentJob && (
        <div style={S.breadcrumb}>
          Change order of{" "}
          <button
            type="button"
            onClick={() => onOpenJob?.(parentJob.id)}
            style={{ ...S.breadcrumbLink, background: "none", border: "none", padding: 0, fontFamily: font, fontSize: "inherit", cursor: "pointer" }}
          >
            {parentJob.clientName} · {parentJob.jobId}
          </button>
        </div>
      )}

      <StatusBar
        job={job}
        canChangeStatus={access.canChangeStatus}
        canSeeMoney={access.canSeeMoney}
        editing={editing}
        onSetStatus={(s) => updateJobStatus(job.id, s)}
        onToggleMilestone={(m) => toggleJobMilestone(job.id, m)}
      />

      <div className="jd-tabbar-row" style={S.tabbarRow}>
        <div className="jd-tabbar" style={S.tabbar}>
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                ...S.tab,
                color: activeTabKey === t.key ? C.text : C.muted,
                borderBottomColor: activeTabKey === t.key ? C.text : "transparent",
                fontWeight: activeTabKey === t.key ? 500 : 400,
              }}
            >{t.label}</button>
          ))}
        </div>
        {access.canEditJobCard && (
          <ReadEditToggle editing={editing} onToggle={() => setEditing(e => !e)} />
        )}
      </div>

      {activeTabKey === "jobCard" ? (
        <JobCard
          job={job}
          contacts={contacts}
          clients={clients}
          amendVersions={amendVersions}
          canSeeMoney={access.canSeeMoney}
          canSeeContacts={access.canSeeContacts}
          canSeeNotes={access.canSeeNotes}
          canSeeInternalMeta={access.canSeeInternalMeta}
          canEditJobCard={access.canEditJobCard}
          canExportJob={access.canExportJob}
          canManageJobs={access.canManageJobs}
          editing={editing}
          setEditing={setEditing}
          upd={upd}
          applyStudio={applyStudio}
          updateJob={updateJob}
          deleteJob={deleteJob}
          unbookJob={unbookJob}
          unlinkQuote={unlinkQuote}
          confirm={confirm}
          setConfirm={setConfirm}
          selectedSessionId={selectedSessionId}
          setSelectedSessionId={setSelectedSessionId}
          onOpenVersions={() => setShowQuoteVersions(true)}
        />
      ) : activeTabKey === "delivReq" ? (
        <DeliverableRequirementsTab
          job={job}
          updateJobFields={(patch) => updateJob(job.id, patch)}
          editing={editing}
        />
      ) : activeTabKey === "preProd" ? (
        <>
          <SessionTabPicker
            job={job}
            sessions={tabSelectableSessions}
            selectedSession={sessionForTabs}
            onSelect={setSelectedSessionId}
            mode="prep"
          />
          <JobPrepTab
            job={tabJob}
            editing={editing}
            isCrew={isCrew}
            canEditShootWork={access.canEditShootWork}
            canManageSettings={access.canManageJobs}
            updateJobFields={updateTabJob}
          />
        </>
      ) : activeTabKey === "capture" ? (
        <>
          <SessionTabPicker
            job={job}
            sessions={tabSelectableSessions}
            selectedSession={sessionForTabs}
            onSelect={setSelectedSessionId}
            mode="capture"
          />
          <CaptureLog
            job={tabJob}
            editing={editing}
            isCrew={isCrew}
            canEditShootWork={access.canEditShootWork}
            canManageJobs={access.canManageJobs}
            currentSessionId={sessionForTabs?.id || null}
            currentUserLabel={currentUserLabel}
            onRestoreRow={restoreCaptureRow}
            updateJobFields={updateTabJob}
            // Slice 3.2: "Process" on a rig capture (the server allows admin and rig_operator).
            canStartWork={access.isAdmin || access.isCrew}
          />
        </>
      ) : activeTabKey === "processing" ? (
        <>
          <SessionTabPicker
            job={job}
            sessions={tabSelectableSessions}
            selectedSession={sessionForTabs}
            onSelect={setSelectedSessionId}
            mode="processing"
          />
          <ProcessingTab
            job={tabJob}
            editing={editing}
            updateJobFields={updateTabJob}
          />
        </>
      ) : activeTabKey === "delivery" ? (
        <>
          <SessionTabPicker
            job={job}
            sessions={tabSelectableSessions}
            selectedSession={sessionForTabs}
            onSelect={setSelectedSessionId}
            mode="delivery"
          />
          <DeliveryTab
            job={tabJob}
            editing={editing}
            isCrew={isCrew}
            canSeeMoney={access.canSeeMoney}
            updateJobFields={updateTabJob}
          />
          {/* SafeShare (slice 4.1): the job's assets, read-only, admins only. */}
          {access.isAdmin && job?.id ? <AssetsList jobId={job.id} /> : null}
        </>
      ) : activeTabKey === "docsFiles" ? (
        // Job-level (not session-scoped): docs, conversations, and files
        // belong to the whole job, so this gets the root job + plain updates.
        <DocsFilesTab
          job={job}
          editing={editing}
          updateJobFields={(patch) => updateJob(job.id, patch)}
          user={user}
        />
      ) : activeTabKey === "financials" ? (
        <FinancialsTab
          job={job}
          linkedQuote={linkedQuote}
          editing={editing}
          onOpenBuilder={onOpenBuilder}
          onOpenVersions={() => setShowQuoteVersions(true)}
        />
      ) : activeTabKey === "files" ? (
        <CrewFilesTab job={job} />
      ) : (
        <PlaceholderTab label={activeTabSpec.label} ships={activeTabSpec.ships} />
      )}
      {access.canSeeMoney && showQuoteVersions && linkedQuote && (
        <QuoteVersionHistoryModal
          quote={linkedQuote}
          linkedJob={job}
          onClose={() => setShowQuoteVersions(false)}
          // Cancel in the Unsaved dialog goes back to the quote open in the builder.
          onOpenBuilder={() => openUnlessCancelled(() => loadQuote(linkedQuote), onOpenBuilder, onOpenBuilder)}
        />
      )}
    </div>
  );
}

const SESSION_COLORS = SESSION_TYPE_COLORS(C);

function SessionTabPicker({ job, sessions, selectedSession, onSelect, mode }) {
  if (!job?.multiDay || sessions.length <= 1) return null;
  const label = mode === "capture" ? "Capture Session" : mode === "processing" ? "Processing Session" : mode === "delivery" ? "Delivery Session" : "Session";
  return (
    <div style={S.sessionPicker}>
      <div style={S.sessionPickerLabel}>{label}</div>
      <div style={S.sessionPickerBtns}>
        {sessions.map((s, i) => {
          const active = s?.id === selectedSession?.id;
          const color = SESSION_COLORS[s?.sessionType] || C.muted;
          return (
            <button
              key={s?.id || `session-tab-${i}`}
              onClick={() => onSelect(s?.id || null)}
              style={{
                ...S.sessionPickerBtn,
                borderColor: active ? `${color}80` : C.border,
                background: active ? `${color}14` : "transparent",
                color: active ? color : C.muted,
              }}
            >
              <span style={{ ...S.sessionPickerType, color }}>{SESSION_TYPE_LABELS[s?.sessionType] || s?.sessionType || "Session"}</span>
              <span>{fmtDate(s?.date, FMT_DATE_DOW) || "TBD"}</span>
              {(s?.callTime || s?.wrapTime) && (
                <span style={S.sessionPickerTime}>
                  {[fmtTime(s?.callTime), fmtTime(s?.wrapTime)].filter(t => t && t !== "—").join(" - ")}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StatusBar({ job, canChangeStatus, canSeeMoney, editing, onSetStatus, onToggleMilestone }) {
  const jobIdx = JOB_STATUSES.indexOf(job.status);
  // Status is read-only for non-admins; admins change it only while in edit mode.
  const allowStatusChange = canChangeStatus && editing;
  // Invoiced/Paid are money milestones — only money-capable roles may toggle them.
  const canToggleMilestone = canSeeMoney && editing;

  return (
    <div style={S.statusbar}>
      <div style={S.statusTrack}>
        {JOB_STATUSES.map((s, i) => {
          const clr = JSC[s] || C.muted;
          const active = i === jobIdx;
          const past = i < jobIdx;
          return (
            <div
              key={s}
              onClick={() => allowStatusChange && onSetStatus(s)}
              style={{
                flex: 1, height: 5, borderRadius: 3,
                background: active ? clr : past ? `${clr}80` : C.border,
                boxShadow: active ? `0 0 6px ${clr}40` : "none",
                cursor: allowStatusChange ? "pointer" : "default",
                transition: "background 0.15s ease",
              }}
            />
          );
        })}
      </div>
      <div style={S.statusLabels}>
        {JOB_STATUSES.map((s, i) => {
          const clr = JSC[s] || C.muted;
          const active = i === jobIdx;
          return (
            <div
              key={s}
              onClick={() => allowStatusChange && onSetStatus(s)}
              style={{
                flex: 1, textAlign: "center",
                fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.08em",
                color: active ? clr : C.muted,
                fontWeight: active ? 500 : 400,
                cursor: allowStatusChange ? "pointer" : "default",
                userSelect: "none",
              }}
            >{JOB_STATUS_LABELS[s]}</div>
          );
        })}
      </div>
      {canSeeMoney && (
        <div style={S.statusMilestones}>
          <MilestoneBtn
            label="Invoiced"
            active={!!job.invoiced}
            color={JSC.invoiced}
            disabled={!canToggleMilestone}
            onClick={() => onToggleMilestone("invoiced")}
          />
          <MilestoneBtn
            label="Paid"
            active={!!job.paid}
            color={JSC.paid}
            disabled={!canToggleMilestone || !job.invoiced}
            onClick={() => onToggleMilestone("paid")}
          />
        </div>
      )}
    </div>
  );
}

function MilestoneBtn({ label, active, color, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "5px 11px", borderRadius: 6,
        fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em",
        border: `1px solid ${active ? `${color}50` : C.border}`,
        background: active ? `${color}15` : "transparent",
        color: active ? color : C.muted,
        fontWeight: active ? 500 : 400,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled && !active ? 0.5 : 1,
        fontFamily: font,
        transition: "all 0.15s ease",
      }}
    >
      {active && <span style={{ fontSize: 10 }}>✓</span>}
      {label}
    </button>
  );
}

function JobCard({
  job, contacts, clients, amendVersions,
  canSeeMoney, canSeeContacts, canSeeNotes, canSeeInternalMeta, canEditJobCard,
  canExportJob, canManageJobs, editing, setEditing,
  upd, applyStudio, updateJob,
  deleteJob, unbookJob, unlinkQuote,
  confirm, setConfirm,
  selectedSessionId, setSelectedSessionId,
  onOpenVersions,
}) {
  const clientsSorted = useMemo(() => sortByName(clients), [clients]);
  const contactsSorted = useMemo(() => sortByName(contacts), [contacts]);
  const [pendingStudio, setPendingStudio] = useState(false);
  const [confirmDeleteSessionId, setConfirmDeleteSessionId] = useState(null);

  const sortedSessions = useMemo(() => sortSessions(job.sessions || []), [job.sessions]);
  const { selectedIndex, selectedSession } = useMemo(() => {
    if (selectedSessionId == null) return { selectedIndex: null, selectedSession: null };
    const i = sortedSessions.findIndex(s => s?.id === selectedSessionId);
    return i === -1
      ? { selectedIndex: null, selectedSession: null }
      : { selectedIndex: i, selectedSession: sortedSessions[i] };
  }, [sortedSessions, selectedSessionId]);
  const uniqueDateCount = useMemo(() => {
    const set = new Set();
    sortedSessions.forEach(s => { if (s?.date) set.add(s.date); });
    return set.size;
  }, [sortedSessions]);

  // Route per-session writes through scopeJobToSession.buildUpdate — single source of truth for
  // splitting session fields from root fields. J5 will reuse the same function for Capture / Processing / Delivery tabs.
  const scope = useMemo(
    () => selectedSession ? scopeJobToSession(job, selectedSession.id) : null,
    [job, selectedSession],
  );

  // Promote root schedule + location into a single session, then clear root gcal tracking.
  // Reverse path (demote) only runs when 0-1 sessions exist so we can safely hoist one set of fields.
  const toggleMultiDay = () => {
    if (!job.multiDay) {
      const sess = {
        ...BLANK_SESSION(),
        date: job.captureDate || "",
        callTime: job.callTime || "",
        wrapTime: job.wrapTime || "",
        gcalEventId: job.gcalEventId || null,
        gcalSyncedFingerprint: job.gcalSyncedFingerprint || null,
      };
      SESSION_CAPTURE_FIELDS.forEach(f => { if (job[f] !== undefined && job[f] !== "" && !(Array.isArray(job[f]) && job[f].length === 0)) sess[f] = job[f]; });
      SESSION_LOCATION_FIELDS.forEach(f => { if (job[f]) sess[f] = job[f]; });
      sess.isStudio = !!job.isStudio;
      const sessions = job.captureDate ? [sess] : [];
      updateJob(job.id, { multiDay: true, sessions, gcalEventId: null, gcalSyncedFingerprint: null });
      setSelectedSessionId(sessions[0]?.id || null);
    } else if ((job.sessions || []).length <= 1) {
      const sess = (job.sessions || [])[0] || {};
      const hoisted = {};
      SESSION_CAPTURE_FIELDS.forEach(f => { if (sess[f] !== undefined) hoisted[f] = sess[f]; });
      SESSION_LOCATION_FIELDS.forEach(f => { if (sess[f] !== undefined) hoisted[f] = sess[f]; });
      if (sess.isStudio !== undefined) hoisted.isStudio = sess.isStudio;
      if (sess.callTime !== undefined) hoisted.callTime = sess.callTime;
      if (sess.wrapTime !== undefined) hoisted.wrapTime = sess.wrapTime;
      if (sess.gcalEventId !== undefined) hoisted.gcalEventId = sess.gcalEventId;
      if (sess.gcalSyncedFingerprint !== undefined) hoisted.gcalSyncedFingerprint = sess.gcalSyncedFingerprint;
      if (sess.date) hoisted.captureDate = sess.date;
      updateJob(job.id, { multiDay: false, sessions: [], ...hoisted });
      setSelectedSessionId(null);
    }
  };

  const persistSessions = (next, trackedId) => {
    const sorted = sortSessions(next);
    updateJob(job.id, { sessions: sorted });
    if (trackedId != null) {
      const still = sorted.find(s => s?.id === trackedId);
      setSelectedSessionId(still ? trackedId : null);
    }
  };

  const writeSessionFields = (patch) => {
    if (!scope) return;
    const update = scope.buildUpdate(patch);
    if (update.sessions) update.sessions = sortSessions(update.sessions);
    updateJob(job.id, update);
  };
  const writeSessionField = (field, value) => writeSessionFields({ [field]: value });

  const requestStudio = (goStudio) => {
    if (goStudio && (job.locationVenue || job.location || job.locationAddress)) {
      setPendingStudio(true);
      return;
    }
    applyStudio(goStudio);
  };

  // A job edited from outside the Suite (the /crm job mode) leaves its calendar
  // event marked stale (needsCalendarReconcile). Push it once per job opened
  // here; the write-back clears the mark, and a failed push simply leaves it
  // for the next open.
  const { reconcileJobCalendar } = useData();
  // Remembers the job an attempt is IN FLIGHT or FAILED for. Cleared as soon as
  // the mark is gone (the write-back), so a later mark on the same open job
  // fires again; a job that never needed anything is never remembered, so a
  // mark that arrives while the job is already open is not skipped.
  const reconciledFor = useRef(null);
  useEffect(() => {
    if (!job?.id) return;
    if (!needsCalendarReconcile(job)) { if (reconciledFor.current === job.id) reconciledFor.current = null; return; }
    if (reconciledFor.current === job.id) return;
    reconciledFor.current = job.id;
    reconcileJobCalendar?.(job);
  }, [job, reconcileJobCalendar]);

  // First time an admin enters edit mode on a job that only has the legacy linkedEntities
  // shape, upgrade it to linkedContacts so the editor works consistently across old + new jobs.
  useEffect(() => {
    if (!editing || !canEditJobCard) return;
    const hasLinked = (job.linkedContacts || []).length > 0;
    const hasLegacy = (job.linkedEntities || []).length > 0;
    if (hasLinked || !hasLegacy) return;
    const migrated = (job.linkedEntities || [])
      .filter(le => le.role !== "billed_client" && le.entityId)
      .map(le => ({ contactId: le.entityId, role: le.role || "contact" }));
    if (migrated.length) updateJob(job.id, { linkedContacts: migrated });
  }, [editing, canEditJobCard, job.id, job.linkedContacts, job.linkedEntities, updateJob]);

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 280px", minWidth: 0 }}>
          {editing && canEditJobCard ? (
            <>
              <input
                list="jobcard-clients"
                style={{ ...inpStyle, fontSize: 18, fontWeight: 500, marginBottom: 8, padding: "8px 12px" }}
                value={job.clientName || ""}
                onChange={e => {
                  const v = e.target.value;
                  const match = clientsSorted.find(cl => (cl.name || "").toLowerCase() === v.toLowerCase().trim());
                  if (match) {
                    updateJob(job.id, { clientName: v, clientId: match.id });
                  } else if (job.clientId) {
                    // Name diverges from the linked client — drop clientId to keep name and id in sync.
                    updateJob(job.id, { clientName: v, clientId: null });
                  } else {
                    upd("clientName", v);
                  }
                }}
                placeholder="Client name..."
              />
              <datalist id="jobcard-clients">
                {clientsSorted.map(cl => <option key={cl.id} value={cl.name || ""} />)}
              </datalist>
              <input
                list="jobcard-contacts"
                style={{ ...inpStyle, fontSize: 13, padding: "7px 12px" }}
                value={job.contactName || ""}
                onChange={e => {
                  const v = e.target.value;
                  const match = contactsSorted.find(ct => (ct.name || "").toLowerCase() === v.toLowerCase().trim());
                  const oldName = (job.contactName || "").toLowerCase().trim();
                  const prior = oldName
                    ? contactsSorted.find(c => (c.name || "").toLowerCase() === oldName)
                    : null;
                  const hadAutoLink = !!prior && (job.linkedContacts || []).some(lc => lc.contactId === prior.id && lc.role === "contact");
                  // Drop the prior auto-link whenever contactName moves away from it — whether the
                  // new name resolves to a different contact or to nothing. Keeps linkedContacts
                  // from accumulating stale "contact"-role entries across repeated edits.
                  const shouldDropPrior = hadAutoLink && (!match || match.id !== prior.id);
                  const currentLinked = job.linkedContacts || [];
                  let nextLinked = shouldDropPrior
                    ? currentLinked.filter(lc => !(lc.contactId === prior.id && lc.role === "contact"))
                    : currentLinked;
                  const alreadyLinked = match && nextLinked.some(lc => lc.contactId === match.id);
                  if (match && !alreadyLinked) {
                    nextLinked = [...nextLinked, { contactId: match.id, role: "contact" }];
                  }
                  if (nextLinked === currentLinked) {
                    upd("contactName", v);
                  } else {
                    updateJob(job.id, { contactName: v, linkedContacts: nextLinked });
                  }
                }}
                placeholder="Contact name..."
              />
              <datalist id="jobcard-contacts">
                {contactsSorted.map(ct => <option key={ct.id} value={ct.name || ""} />)}
              </datalist>
            </>
          ) : (
            <>
              <div style={S.clientName}>{job.clientName || "—"}</div>
              {canSeeContacts && job.contactName && <div style={S.contactName}>{job.contactName}</div>}
            </>
          )}
          {canSeeMoney && amendVersions > 0 && (
            <button type="button" style={{ ...S.amendBadge, cursor: "pointer", fontFamily: font }} onClick={onOpenVersions}>
              Amended · {amendVersions} version{amendVersions === 1 ? "" : "s"}
            </button>
          )}
          {!editing && job.description && (
            <div style={S.descriptionRead}>{job.description}</div>
          )}
        </div>

        <div style={S.identityRight}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={S.jobId}>{job.jobId || "no-id"}</span>
            {canExportJob && (
              <button
                style={{ ...btnS, padding: "3px 9px", fontSize: 10 }}
                onClick={() => exportJobJSON(job)}
              >Export JSON</button>
            )}
          </div>

          {canManageJobs && editing && confirm === null && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {job.quoteId && (
                <button style={{ ...btnS, padding: "4px 10px", fontSize: 10.5 }} onClick={() => setConfirm("unlink")}>Unlink Quote</button>
              )}
              {job.quoteId && (
                <button
                  style={{ ...btnS, padding: "4px 10px", fontSize: 10.5, color: C.amber, borderColor: `${C.amber}60` }}
                  onClick={() => setConfirm("unbook")}
                >Unbook</button>
              )}
              <button style={{ ...btnD, padding: "4px 10px", fontSize: 10.5 }} onClick={() => setConfirm("delete")}>Delete</button>
            </div>
          )}

          {confirm === "unlink" && canManageJobs && (
            <ConfirmInline
              tone="warn"
              message="Unlink quote? Job stays, quote returns to Quoted."
              yesLabel="Yes, unlink"
              onYes={() => { unlinkQuote(job.id); setConfirm(null); }}
              onCancel={() => setConfirm(null)}
            />
          )}
          {confirm === "unbook" && canManageJobs && (
            <ConfirmInline
              tone="warn"
              message="Send back to quotes? Removes this job."
              yesLabel="Yes, unbook"
              onYes={() => { unbookJob(job.id); setConfirm(null); }}
              onCancel={() => setConfirm(null)}
            />
          )}
          {confirm === "delete" && canManageJobs && (
            <ConfirmInline
              tone="danger"
              message="Delete this job? Moves to Trash."
              yesLabel="Yes, delete"
              onYes={() => { deleteJob(job.id); setConfirm(null); }}
              onCancel={() => setConfirm(null)}
            />
          )}
        </div>
      </div>

      {/* ── Description (edit-only; read version lives under identity) ── */}
      {editing && canEditJobCard && (
        <>
          <Divider />
          <div>
            <label style={lblStyle}>Description</label>
            <textarea
              style={{ ...inpStyle, resize: "vertical", fontSize: 12.5, minHeight: 60 }}
              rows={2}
              value={job.description || ""}
              onChange={e => upd("description", e.target.value)}
              placeholder="Job description..."
            />
          </div>
        </>
      )}

      {/* ── Schedule — single-day grid or multi-day session strip ── */}
      <Divider />
      <div style={S.sectionHead}>
        <span style={lblStyle}>Schedule</span>
        {job.multiDay && sortedSessions.length > 0 && (
          <span style={S.multiBadge}>
            Multi-day · {sortedSessions.length} session{sortedSessions.length === 1 ? "" : "s"} · {uniqueDateCount} calendar day{uniqueDateCount === 1 ? "" : "s"}
          </span>
        )}
        {editing && canManageJobs && (
          <span style={{ marginLeft: "auto" }}>
            <MultiDayToggle
              on={!!job.multiDay}
              disabled={!!job.multiDay && (job.sessions || []).length > 1}
              onClick={toggleMultiDay}
            />
          </span>
        )}
      </div>

      {job.multiDay ? (
        <div>
          <SessionStrip
            sortedSessions={sortedSessions}
            selectedIndex={selectedIndex}
            canEdit={editing && canManageJobs}
            onSelect={(idx) => {
              if (idx === null) { setSelectedSessionId(null); return; }
              setSelectedSessionId(sortedSessions[idx]?.id || null);
            }}
            onRequestDelete={(id) => setConfirmDeleteSessionId(id)}
            onImmediateDelete={(id) => {
              const next = (job.sessions || []).filter(s => s?.id !== id);
              persistSessions(next, selectedSessionId === id ? null : selectedSessionId);
              if (selectedSessionId === id) setSelectedSessionId(null);
            }}
          />
          {editing && canManageJobs && (
            <AddDayMenu
              job={job}
              selectedSession={selectedIndex}
              sortedSessions={sortedSessions}
              onAdd={(sortedNext, newId) => {
                updateJob(job.id, { sessions: sortedNext });
                setSelectedSessionId(newId);
              }}
            />
          )}
          {selectedSession && (
            <SessionEditor
              job={job}
              session={selectedSession}
              canEdit={editing && canManageJobs}
              canManageSessions={canManageJobs}
              onWriteField={writeSessionField}
              onWriteFields={writeSessionFields}
              onClose={() => setSelectedSessionId(null)}
            />
          )}
          {confirmDeleteSessionId && canManageJobs && (
            <DeleteSessionConfirm
              session={(job.sessions || []).find(s => s?.id === confirmDeleteSessionId)}
              onCancel={() => setConfirmDeleteSessionId(null)}
              onConfirm={() => {
                const removeId = confirmDeleteSessionId;
                const next = (job.sessions || []).filter(s => s?.id !== removeId);
                const tracked = selectedSessionId === removeId ? null : selectedSessionId;
                persistSessions(next, tracked);
                if (selectedSessionId === removeId) setSelectedSessionId(null);
                setConfirmDeleteSessionId(null);
              }}
            />
          )}
        </div>
      ) : (
        <div style={S.gridSchedule}>
          <ScheduleField label="Job Date" type="date" value={job.captureDate || ""} editing={editing && canEditJobCard} onChange={v => upd("captureDate", v)} readValue={fmtDate(job.captureDate, FMT_DATE_LONG)} />
          <ScheduleField label="Scheduled Time" type="time" value={job.scheduledTime || ""} editing={editing && canEditJobCard} onChange={v => upd("scheduledTime", v)} readValue={fmtTime(job.scheduledTime)} />
          <ScheduleField label="Call Time" type="time" value={job.callTime || ""} editing={editing && canEditJobCard} onChange={v => upd("callTime", v)} readValue={fmtTime(job.callTime)} />
          <ScheduleField label="Wrap Time" type="time" value={job.wrapTime || ""} editing={editing && canEditJobCard} onChange={v => upd("wrapTime", v)} readValue={fmtTime(job.wrapTime)} />
        </div>
      )}

      {/* Location sits per-session for multi-day jobs — only show the root editor on single-day. */}
      {!job.multiDay && (
        <>
          <Divider />
          <div style={S.sectionHead}>
            <span style={lblStyle}>Location</span>
            {editing && canEditJobCard && (
              <StudioToggle isStudio={!!job.isStudio} onChange={requestStudio} />
            )}
            {pendingStudio && (
              <ConfirmInline
                tone="warn"
                message="Replace current location with the studio address?"
                yesLabel="Yes, use Studio"
                onYes={() => { applyStudio(true); setPendingStudio(false); }}
                onCancel={() => setPendingStudio(false)}
              />
            )}
          </div>
          {editing && canEditJobCard ? (
            <LocationInputs value={job} onField={upd} disabled={!!job.isStudio} />
          ) : (
            <LocationRead job={job} />
          )}
        </>
      )}

      {canSeeInternalMeta && (
        <>
          <Divider />
          <div style={S.sectionHead}>
            <span style={lblStyle}>Internal Metadata</span>
          </div>
          <div style={S.gridMeta}>
            <MetaField
              label="Client Code"
              editing={editing}
              valueRead={<span style={{ fontFamily: mono }}>{job.clientCode || "—"}</span>}
              inputProps={{
                style: { ...inpStyle, fontFamily: mono },
                value: job.clientCode || "",
                maxLength: 7,
                placeholder: "e.g. chromar",
                onChange: e => upd("clientCode", e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 7)),
              }}
            />
            <MetaField
              label="Production / Project"
              editing={editing}
              valueRead={job.project || "—"}
              inputProps={{
                style: inpStyle,
                value: job.project || "",
                placeholder: "e.g. Spring Lookbook",
                onChange: e => upd("project", e.target.value),
              }}
            />
            <div>
              <label style={lblStyle}>Industry</label>
              {editing ? (
                <select
                  style={{ ...inpStyle, cursor: "pointer" }}
                  value={job.industry || "other"}
                  onChange={e => upd("industry", e.target.value)}
                >
                  {Object.entries(INDUSTRY_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              ) : (
                <div style={S.val}>{INDUSTRY_LABELS[job.industry] || job.industry || "—"}</div>
              )}
            </div>
            <div>
              <label style={lblStyle}>Quoted</label>
              <div style={{ ...S.val, color: C.muted }}>{fmtDate(job.quoteDate, FMT_DATE_FULL) || "—"}</div>
            </div>
          </div>
        </>
      )}

      {canSeeContacts && (
        <>
          <Divider />
          <LinkedContacts
            job={job}
            contacts={contacts}
            contactsSorted={contactsSorted}
            clients={clients}
            editing={editing}
            onUpdate={patch => updateJob(job.id, patch)}
          />
        </>
      )}

      {canSeeNotes && (editing || job.notes) && (
        <>
          <Divider />
          <div style={S.sectionHead}>
            <span style={lblStyle}>Notes</span>
          </div>
          {editing ? (
            <textarea
              style={{ ...inpStyle, resize: "vertical", fontSize: 12.5, minHeight: 70 }}
              rows={3}
              value={job.notes || ""}
              onChange={e => upd("notes", e.target.value)}
              placeholder="General job notes..."
            />
          ) : (
            <div style={S.notesRead}>{job.notes}</div>
          )}
        </>
      )}
    </div>
  );
}

function Divider() {
  return <div style={{ height: 1, background: C.border, margin: "18px 0" }} />;
}

function ReadEditToggle({ editing, onToggle }) {
  return (
    <button
      onClick={onToggle}
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: "5px 12px", background: "transparent",
        border: `1px solid ${editing ? `${C.amber}55` : C.border}`,
        borderRadius: 6, cursor: "pointer", fontFamily: font, fontSize: 10.5,
        transition: "border-color 0.15s ease",
      }}
    >
      <span style={{ color: editing ? C.muted : C.text, fontWeight: editing ? 400 : 500 }}>Read</span>
      <span style={{
        width: 28, height: 14, borderRadius: 7, position: "relative",
        background: editing ? `${C.amber}35` : C.border, transition: "background 0.2s ease",
      }}>
        <span style={{
          width: 10, height: 10, borderRadius: 5, position: "absolute",
          top: 2, left: editing ? 16 : 2,
          background: editing ? C.amber : C.muted, transition: "left 0.2s ease",
        }} />
      </span>
      <span style={{ color: editing ? C.amber : C.muted, fontWeight: editing ? 500 : 400 }}>Edit</span>
    </button>
  );
}

function MultiDayToggle({ on, disabled, onClick }) {
  const activeColor = on ? C.blue : C.muted;
  return (
    <button
      onClick={disabled ? undefined : onClick}
      title={disabled ? "Remove extra sessions first" : (on ? "Switch to single-day" : "Switch to multi-day")}
      style={{
        padding: "3px 10px", fontSize: 9.5, fontWeight: 400,
        borderRadius: 3, cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: font, letterSpacing: "0.06em", textTransform: "uppercase",
        border: `1px solid ${on ? `${C.blue}55` : C.border}`,
        background: on ? `${C.blue}0D` : "transparent",
        color: activeColor,
        opacity: disabled ? 0.5 : 1,
      }}
    >Multi-day</button>
  );
}

function StudioToggle({ isStudio, onChange }) {
  return (
    <div style={S.studioToggle}>
      <button
        onClick={() => onChange(false)}
        style={{
          ...S.studioBtn,
          background: !isStudio ? C.surface : "transparent",
          color: !isStudio ? C.text : C.muted,
          fontWeight: !isStudio ? 500 : 400,
        }}
      >On-location</button>
      <button
        onClick={() => onChange(true)}
        style={{
          ...S.studioBtn,
          background: isStudio ? C.surface : "transparent",
          color: isStudio ? C.text : C.muted,
          fontWeight: isStudio ? 500 : 400,
        }}
      >Studio</button>
    </div>
  );
}

function ScheduleField({ label, type, value, editing, onChange, readValue }) {
  return (
    <div>
      <label style={lblStyle}>{label}</label>
      {editing ? (
        <input type={type} style={inpStyle} value={value} onChange={e => onChange(e.target.value)} />
      ) : (
        <div style={{ ...S.val, color: readValue ? C.text : C.muted, fontStyle: readValue ? "normal" : "italic" }}>
          {readValue || "TBD"}
        </div>
      )}
    </div>
  );
}

function MetaField({ label, editing, valueRead, inputProps }) {
  return (
    <div>
      <label style={lblStyle}>{label}</label>
      {editing ? <input {...inputProps} /> : <div style={S.val}>{valueRead || "—"}</div>}
    </div>
  );
}

function LocationRead({ job }) {
  const venue = job.isStudio ? (job.locationVenue || STUDIO_ADDRESS_PARTS.venue) : (job.locationVenue || job.location || "");
  const addr = [job.locationAddress, job.locationCity, job.locationState, job.locationZip].filter(Boolean).join(", ");
  const mapQuery = addressMapQuery(job);
  const showMap = !job.isStudio && mapQuery && mapQuery !== "In Studio";

  return (
    <div>
      <div style={S.val}>{venue || "—"}</div>
      {addr && <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{addr}</div>}
      {showMap && (
        <a
          href={"https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(mapQuery)}
          target="_blank"
          rel="noopener noreferrer"
          style={S.mapLink}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          Open in Google Maps
        </a>
      )}
    </div>
  );
}

function ConfirmInline({ tone, message, yesLabel, onYes, onCancel }) {
  const colors = tone === "danger"
    ? { bg: "rgba(239,68,68,0.06)", border: "rgba(239,68,68,0.25)", fg: C.danger }
    : { bg: `${C.amber}14`,           border: `${C.amber}40`,        fg: C.amber  };
  const yesStyle = tone === "danger" ? btnD : { ...btnS, color: C.amber, borderColor: `${C.amber}60` };
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      padding: "6px 10px", borderRadius: 6,
      background: colors.bg, border: `1px solid ${colors.border}`,
      flexWrap: "wrap", maxWidth: 320, justifyContent: "flex-end",
    }}>
      <span style={{ fontSize: 10.5, color: colors.fg }}>{message}</span>
      <button style={{ ...yesStyle, padding: "3px 9px", fontSize: 10 }} onClick={onYes}>{yesLabel}</button>
      <button style={{ ...btnS, padding: "3px 9px", fontSize: 10 }} onClick={onCancel}>Cancel</button>
    </div>
  );
}

function PlaceholderTab({ label, ships, subline }) {
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
      padding: "40px 24px", textAlign: "center", color: C.muted, fontSize: 13,
    }}>
      <div style={{ fontSize: 14, color: C.text, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 11.5 }}>{subline || `Ships in session ${ships || "a later session"}.`}</div>
    </div>
  );
}

function sortByName(arr) {
  return [...(arr || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
}

function exportJobJSON(job) {
  const a = document.createElement("a");
  const url = URL.createObjectURL(new Blob([JSON.stringify(job, null, 2)], { type: "application/json" }));
  a.href = url;
  a.download = `${job.jobId || job.id}.json`;
  a.click();
  // Defer revoke — some browsers abort the download if the blob URL is released synchronously.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

const S = {
  jobHeader: {
    display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 14,
  },
  jobHeaderClient: {
    fontSize: 16, fontWeight: 500, color: C.text, letterSpacing: "-0.01em",
  },
  jobHeaderMeta: {
    fontFamily: mono, fontSize: 12, color: C.muted,
  },
  changeOrderBadge: {
    fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em",
    color: JSC.in_progress, background: `${JSC.in_progress}1F`, borderRadius: 4, padding: "2px 7px",
  },
  breadcrumb: {
    fontSize: 11, color: C.muted, marginBottom: 10,
  },
  breadcrumbLink: {
    color: C.text, textDecoration: "underline", cursor: "pointer",
  },

  statusbar: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: 12,
  },
  statusTrack: { display: "flex", gap: 3 },
  statusLabels: { display: "flex", gap: 3, marginTop: 8 },
  statusMilestones: {
    display: "flex", gap: 8,
    marginTop: 12, paddingTop: 10,
    borderTop: `1px solid ${C.border}`,
  },

  tabbarRow: {
    display: "flex", alignItems: "flex-end", justifyContent: "space-between",
    gap: 16, marginBottom: 18,
  },
  tabbar: {
    display: "flex", gap: 2,
    flex: 1, minWidth: 0,
    borderBottom: `1px solid ${C.border}`,
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },
  tab: {
    padding: "9px 14px",
    background: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    fontFamily: font,
    fontSize: 12,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  sessionPicker: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 12px",
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    background: C.surface,
    marginBottom: 14,
    flexWrap: "wrap",
  },
  sessionPickerLabel: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: C.muted,
    fontWeight: 400,
  },
  sessionPickerBtns: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  sessionPickerBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "5px 9px",
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    fontSize: 11,
    fontFamily: font,
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s",
  },
  sessionPickerType: {
    fontSize: 9,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontWeight: 500,
  },
  sessionPickerTime: {
    fontSize: 10,
    color: C.muted,
  },

  card: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "22px 24px",
  },

  clientName: { fontSize: 20, fontWeight: 500, letterSpacing: "-0.01em", marginBottom: 2 },
  contactName: { fontSize: 13, color: C.muted },
  descriptionRead: { fontSize: 13, color: C.muted, lineHeight: 1.55, marginTop: 10, maxWidth: 620 },

  amendBadge: {
    display: "inline-flex", alignItems: "center", gap: 5,
    marginTop: 8, padding: "3px 9px",
    background: `${C.amber}15`, border: `1px solid ${C.amber}40`,
    borderRadius: 6, color: C.amber, fontSize: 10, fontWeight: 500,
    textTransform: "uppercase", letterSpacing: "0.06em",
  },

  identityRight: {
    display: "flex", flexDirection: "column", alignItems: "flex-end",
    gap: 10, flexShrink: 0,
  },
  jobId: { fontFamily: mono, fontSize: 12, color: C.muted },

  sectionHead: {
    display: "flex", alignItems: "center", gap: 12,
    marginBottom: 12, flexWrap: "wrap",
  },

  multiBadge: {
    fontSize: 9.5, padding: "2px 7px", borderRadius: 3,
    background: `${C.blue}14`, color: C.blue, fontWeight: 500,
    letterSpacing: "0.02em",
  },

  gridSchedule: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
    gap: 14,
  },
  gridMeta: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 14,
  },

  val: { fontSize: 13, color: C.text, fontWeight: 300, padding: "3px 0" },

  studioToggle: {
    display: "inline-flex",
    background: C.surfaceAlt,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    padding: 2,
  },
  studioBtn: {
    padding: "4px 12px", borderRadius: 4,
    fontSize: 11, fontFamily: font,
    border: "none",
    cursor: "pointer",
    transition: "all 0.1s ease",
  },

  mapLink: {
    display: "inline-flex", alignItems: "center", gap: 6,
    marginTop: 8, padding: "4px 10px",
    borderRadius: 6,
    fontSize: 11, color: C.blue,
    background: `${C.blue}10`,
    border: `1px solid ${C.blue}25`,
    textDecoration: "none",
  },

  notesRead: {
    fontSize: 12.5, color: C.muted, lineHeight: 1.55, whiteSpace: "pre-wrap",
  },
};
